const compareIds = (a: string, b: string): number =>
  a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);

const comparePositions = (a: number | null, b: number | null): number =>
  a === null || b === null ? Number(a === null) - Number(b === null) : a - b;

export interface RankedRole {
  readonly id: string;
  readonly position: number | null;
}

export const compareRoles = (a: RankedRole, b: RankedRole): number =>
  (a.position === null || b.position === null
    ? comparePositions(a.position, b.position)
    : b.position - a.position) || compareIds(a.id, b.id);

export interface PlacedChannel {
  readonly id: string;
  readonly channelType: string | null;
  readonly parentId: string | null;
  readonly position: number | null;
}

interface TypedChannel {
  readonly channelType?: string | null | undefined;
}

const typeOf = (channel: TypedChannel): string =>
  typeof channel.channelType === "string" ? channel.channelType.toLowerCase() : "";

export const isCategoryChannel = (channel: TypedChannel): boolean =>
  typeOf(channel).includes("category");

export const isThreadChannel = (channel: TypedChannel): boolean =>
  typeOf(channel).includes("thread");

const isVoiceChannel = (channel: TypedChannel): boolean =>
  typeOf(channel).includes("voice") || typeOf(channel).includes("stage");

const homeOf = (channel: PlacedChannel, parent: PlacedChannel | undefined): string | null => {
  if (parent === undefined || isCategoryChannel(channel)) return null;
  if (isThreadChannel(channel)) {
    return isThreadChannel(parent) || isCategoryChannel(parent) ? null : parent.id;
  }
  return isCategoryChannel(parent) ? parent.id : null;
};

const compareSiblings = (a: PlacedChannel, b: PlacedChannel): number =>
  Number(isCategoryChannel(a)) - Number(isCategoryChannel(b)) ||
  Number(isVoiceChannel(a)) - Number(isVoiceChannel(b)) ||
  comparePositions(a.position, b.position) ||
  compareIds(a.id, b.id);

export const orderChannels = <T extends PlacedChannel>(channels: readonly T[]): T[] => {
  const byId = new Map(channels.map((channel) => [channel.id, channel]));
  const under = new Map<string | null, T[]>();
  for (const channel of channels) {
    const home = homeOf(
      channel,
      channel.parentId === null ? undefined : byId.get(channel.parentId),
    );
    under.set(home, [...(under.get(home) ?? []), channel]);
  }
  const walk = (home: string | null): T[] =>
    (under.get(home) ?? [])
      .toSorted(compareSiblings)
      .flatMap((channel) => [channel, ...walk(channel.id)]);
  return walk(null);
};
